import { useEffect, useRef, useState } from 'react';
import { Range, getTrackBackground } from 'react-range';
import {
  ScissorsIcon,
  PlusIcon,
  InformationCircleIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';

// Minimum gap enforced between start/end when a user types a value manually —
// the slider itself already can't cross its two handles, but typed input needs
// its own guard against e.g. an end time typed earlier than start.
const MIN_CLIP_SECONDS = 0.5;

function pad(n: number, width = 2) {
  return n.toString().padStart(width, '0');
}

// HH:MM:SS.d — matches the reference design exactly (one decisecond digit).
// Works entirely in integer deciseconds to avoid floating-point rounding
// artifacts (e.g. a stray ".10" at a rollover boundary).
function formatHMSD(totalSeconds: number): string {
  const totalDeciseconds = Math.max(0, Math.round(totalSeconds * 10));
  const deciseconds = totalDeciseconds % 10;
  const totalSecondsInt = Math.floor(totalDeciseconds / 10);
  const hours = Math.floor(totalSecondsInt / 3600);
  const minutes = Math.floor((totalSecondsInt % 3600) / 60);
  const seconds = totalSecondsInt % 60;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}.${deciseconds}`;
}

function parseHMSD(text: string): number | null {
  const match = text.trim().match(/^(\d{1,2}):(\d{1,2}):(\d{1,2})(?:[.,](\d))?$/);
  if (!match) return null;
  const [, h, m, s, d] = match;
  const total = Number(h) * 3600 + Number(m) * 60 + Number(s) + (d ? Number(d) / 10 : 0);
  return Number.isFinite(total) ? total : null;
}

function formatDurationLabel(seconds: number): string {
  if (seconds % 60 === 0) {
    const mins = seconds / 60;
    return `${mins} min${mins === 1 ? '' : 's'}`;
  }
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export interface ClipDetailsModalDraft {
  start: number;
  end: number;
  title: string;
}

interface ClipDetailsModalProps {
  videoUrl: string;
  initialStart: number;
  initialEnd: number;
  initialTitle?: string;
  mode: 'create' | 'edit';
  maxLengthSeconds?: number;
  maxLengthLabel?: string;
  note?: React.ReactNode;
  onSave: (draft: ClipDetailsModalDraft) => void | Promise<void>;
  onCreateAnother?: (draft: ClipDetailsModalDraft) => void;
  onClose: () => void;
}

type FocusedField = 'start' | 'end' | 'duration' | null;

export default function ClipDetailsModal({
  videoUrl,
  initialStart,
  initialEnd,
  initialTitle,
  mode,
  maxLengthSeconds,
  maxLengthLabel,
  note,
  onSave,
  onCreateAnother,
  onClose,
}: ClipDetailsModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [title, setTitle] = useState(initialTitle || '');
  const [start, setStart] = useState(initialStart);
  const [end, setEnd] = useState(initialEnd);
  const [isPlayingClip, setIsPlayingClip] = useState(false);
  const [saving, setSaving] = useState(false);
  const [focusedField, setFocusedField] = useState<FocusedField>(null);

  const [startText, setStartText] = useState(() => formatHMSD(initialStart));
  const [endText, setEndText] = useState(() => formatHMSD(initialEnd));
  const [durationText, setDurationText] = useState(() => formatHMSD(initialEnd - initialStart));

  // Re-sync the text fields whenever the numeric start/end change (slider drag,
  // a sibling field's commit, Play Clip seeking, etc.) — except a field the
  // user is actively typing in, so we don't fight their keystrokes.
  useEffect(() => {
    if (focusedField !== 'start') setStartText(formatHMSD(start));
    if (focusedField !== 'end') setEndText(formatHMSD(end));
    if (focusedField !== 'duration') setDurationText(formatHMSD(end - start));
  }, [start, end, focusedField]);

  const handleLoadedMetadata = () => {
    const duration = videoRef.current?.duration;
    if (duration && Number.isFinite(duration)) {
      setVideoDuration(duration);
      setEnd((prev) => Math.min(prev, duration));
    }
  };

  const commitStart = () => {
    const parsed = parseHMSD(startText);
    if (parsed === null) {
      setStartText(formatHMSD(start));
      return;
    }
    setStart(Math.min(Math.max(0, parsed), end - MIN_CLIP_SECONDS));
  };

  const commitEnd = () => {
    const parsed = parseHMSD(endText);
    if (parsed === null) {
      setEndText(formatHMSD(end));
      return;
    }
    const ceiling = videoDuration || parsed;
    setEnd(Math.max(Math.min(ceiling, parsed), start + MIN_CLIP_SECONDS));
  };

  const commitDuration = () => {
    const parsed = parseHMSD(durationText);
    if (parsed === null) {
      setDurationText(formatHMSD(end - start));
      return;
    }
    const ceiling = videoDuration || start + parsed;
    setEnd(Math.min(ceiling, start + Math.max(MIN_CLIP_SECONDS, parsed)));
  };

  const playClip = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = start;
    v.play();
    setIsPlayingClip(true);
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTimeUpdate = () => {
      if (v.currentTime >= end) {
        v.pause();
        v.currentTime = start;
        setIsPlayingClip(false);
      }
    };
    const onPause = () => setIsPlayingClip(false);
    v.addEventListener('timeupdate', onTimeUpdate);
    v.addEventListener('pause', onPause);
    return () => {
      v.removeEventListener('timeupdate', onTimeUpdate);
      v.removeEventListener('pause', onPause);
    };
  }, [start, end]);

  const currentDraft = (): ClipDetailsModalDraft => ({ start, end, title: title.trim() });

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(currentDraft());
      onClose();
    } catch {
      // The caller is responsible for surfacing its own error (e.g. a toast) —
      // just keep the modal open here so the user can fix input and retry.
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAnother = () => {
    if (!onCreateAnother) return;
    onCreateAnother(currentDraft());
  };

  // Keyboard shortcuts for the modal's lifetime — ignored while the user is
  // typing in a text field so arrow/space/x keep their normal text-editing
  // behavior there.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      const active = document.activeElement;
      const isTyping = active instanceof HTMLElement && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA');
      if (isTyping) return;

      const v = videoRef.current;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (v) v.currentTime = Math.max(0, v.currentTime - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (v) v.currentTime = Math.min(videoDuration || v.currentTime, v.currentTime + 1);
      } else if (e.key === ' ') {
        e.preventDefault();
        if (!v) return;
        if (v.paused) v.play();
        else v.pause();
      } else if ((e.key === 'x' || e.key === 'X') && onCreateAnother) {
        e.preventDefault();
        handleCreateAnother();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoDuration, onCreateAnother, start, end, title, onClose]);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-2xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700">
              <ScissorsIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </span>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Clip Details</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>

        {maxLengthSeconds != null && (
          <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-6 py-2.5 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
            <InformationCircleIcon className="h-4 w-4 shrink-0" />
            <span>
              The maximum clip length{maxLengthLabel ? ` for ${maxLengthLabel}` : ''} is {formatDurationLabel(maxLengthSeconds)}.
            </span>
          </div>
        )}

        {note && (
          <div className="border-b border-amber-200 bg-amber-50 px-6 py-2.5 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
            {note}
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
          <div className="space-y-3">
            <div className="overflow-hidden rounded-lg bg-black">
              <video
                ref={videoRef}
                src={videoUrl}
                controls
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={() => setIsPlayingClip(true)}
                className="aspect-video w-full"
              />
            </div>
            <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <InformationCircleIcon className="h-3.5 w-3.5 shrink-0" />
              Arrow keys to go forward/rewind, Space to play/pause
              {onCreateAnother ? ', "X" to create a clip' : ''}
            </p>
            {onCreateAnother && (
              <button
                type="button"
                onClick={handleCreateAnother}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <PlusIcon className="h-4 w-4" />
                Create Another Clip
              </button>
            )}
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Untitled clip"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="pt-2">
              {videoDuration > 0 ? (
                <Range
                  step={0.1}
                  min={0}
                  max={videoDuration}
                  values={[start, end]}
                  onChange={([newStart, newEnd]) => {
                    setStart(newStart as number);
                    setEnd(newEnd as number);
                  }}
                  renderTrack={({ props, children }) => {
                    return (
                      <div
                        {...props}
                        className="h-1.5 w-full rounded-full"
                        style={{
                          ...props.style,
                          background: getTrackBackground({
                            values: [start, end],
                            colors: ['#e5e7eb', '#4f46e5', '#e5e7eb'],
                            min: 0,
                            max: videoDuration,
                          }),
                        }}
                      >
                        {children}
                      </div>
                    );
                  }}
                  renderThumb={({ props, index }) => {
                    const { key, ...thumbProps } = props;
                    const value = index === 0 ? start : end;
                    return (
                      <div key={key} {...thumbProps} className="relative flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 shadow focus:outline-none focus:ring-2 focus:ring-indigo-300">
                        <span className="absolute -top-7 whitespace-nowrap rounded bg-indigo-600 px-1.5 py-0.5 text-[11px] font-medium text-white">
                          {formatHMSD(value)}
                        </span>
                      </div>
                    );
                  }}
                />
              ) : (
                <div className="h-1.5 w-full animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
              )}
            </div>

            <div className="grid grid-cols-3 gap-3 pt-1">
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Start time</label>
                <input
                  type="text"
                  value={startText}
                  onFocus={() => setFocusedField('start')}
                  onChange={(e) => setStartText(e.target.value)}
                  onBlur={() => {
                    commitStart();
                    setFocusedField(null);
                  }}
                  className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">End time</label>
                <input
                  type="text"
                  value={endText}
                  onFocus={() => setFocusedField('end')}
                  onChange={(e) => setEndText(e.target.value)}
                  onBlur={() => {
                    commitEnd();
                    setFocusedField(null);
                  }}
                  className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Duration</label>
                <input
                  type="text"
                  value={durationText}
                  onFocus={() => setFocusedField('duration')}
                  onChange={(e) => setDurationText(e.target.value)}
                  onBlur={() => {
                    commitDuration();
                    setFocusedField(null);
                  }}
                  className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={playClip}
              disabled={!videoDuration}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PlayIcon className="h-4 w-4" />
              {isPlayingClip ? 'Playing…' : 'Play Clip'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
